<template>
	<templates-limit-exceeded v-if="reachedCampaignLimit" :is-playbooks="true" />
	<core-page v-else>
		<div class="playbooks-page-wrapper dashboard-wrapper" :class="{ previewing: false }">
			<common-page-tabnav current="playbooks" :tabs="tabs" />
			<div v-if="alerts.length" class="container" style="margin: 46px auto 40px">
				<core-alerts :alerts="alerts" />
			</div>
			<div class="playbooks-content">
				<playbooks-intro />
				<core-loading v-if="isLoading" class="playbooks-loading" />
				<div v-else>
					<playbooks-recommendations v-if="hasRecommendations" />
					<playbooks-table />
				</div>
			</div>
			<playbooks-upgrade-modal>
				<core-upgrade-button
					slot="button"
					utm-medium="Playbooks"
					class="button primary"
					target="_blank"
					style="margin: 0 0 15px"
				>
					Unlock Access Now
				</core-upgrade-button>
			</playbooks-upgrade-modal>
			<campaigns-modal-create-campaign :from-playbook="true" />
			<templates-modal-not-connected />
		</div>
	</core-page>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { filtering } from 'optinmonster-js-library/src/vue/playbooks/mixins/filtering';
import { rules } from 'optinmonster-js-library/src/vue/playbooks/mixins/rules';
import { creation } from '@/components/mixins/creation';
import { templatePreviewsSetup } from 'optinmonster-js-library/src/vue/templates/mixins/templatePreviewsSetup';

export default {
	mixins: [filtering, templatePreviewsSetup, creation, rules],
	computed: {
		...mapState(['alerts']),
		...mapState('playbooks', ['playbooks', 'previewing', 'filterOptions', 'selectedPlaybook']),
		...mapGetters(['connected', 'reachedCampaignLimit']),
		...mapGetters('playbooks', ['hasRecommendations']),
		/**
		 * Is Loading
		 *
		 * @returns {boolean} Are playbooks, rules, or filters loading?
		 */
		isLoading() {
			return this.$store.getters.isLoading(['playbooks', 'rules', 'filterOptions']);
		},
		/**
		 * Get the unavailable rules
		 *
		 * @returns {Array} The array of unavailable rules.
		 */
		unavailableRules() {
			return this.selectedPlaybook.rules.filter((rule) => !this.ruleAvailable(rule)) || [];
		},
	},
	watch: {
		/**
		 * Listen for changes to the selected Playbook. This will
		 * then trigger the creation flow.
		 *
		 * @param {Object} playbook The selected Playbook.
		 *
		 * @returns {void}
		 */
		selectedPlaybook(playbook) {
			if (playbook) {
				this.handleUsePlaybook();
			}
		},
	},
	created() {
		this.$store
			.dispatch('campaigns/fetchRulesetData')
			.then((data) => {
				let rules = data.rules;

				rules.push(...data.bigCommerceRules);
				rules.push(...data.eddRules);
				rules.push(...data.shopifyRules);
				rules.push(...data.wooCommerceRules);

				this.setRules(rules);
			})
			.catch(() => {});

		// No filter options? Go get'em.
		if (!this.filterOptions.length) {
			this.$store
				.dispatch('templates/fetchFilterOptions')
				.then(({ body }) => {
					this.setFilterOptions(body);
				})
				.catch(() => {});
		}

		// No Playbooks, get them too.
		if (!this.playbooks.length) {
			this.$store
				.dispatch('playbooks/fetchPlaybooksData')
				.then(() => {
					if (this.$store.getters.connected) {
						this.$store.dispatch('playbooks/fetchRecommendationsData').catch(() => {});
					}
				})
				.catch(() => {});
		}

		this.setDismissedRecommendations();

		this.loadApiScript(
			'omwpapi-playbooks-apijs',
			this.$constants.PLAYBOOKS_PREVIEW_ACCOUNT,
			this.$constants.PLAYBOOKS_PREVIEW_USER
		);
	},
	methods: {
		...mapMutations('playbooks', ['setDismissedRecommendations', 'setRules', 'setFilterOptions', 'setUpgradeRule']),
		/**
		 * Handle clicking the 'Use Playbook' button.
		 *
		 * @returns {void}
		 */
		handleUsePlaybook() {
			this.$store.commit('templates/setActiveTemplate', this.selectedPlaybook);

			if (!this.$store.getters.connected) {
				return this.$modal.show('not-connected');
			}

			if (this.unavailableRules.length) {
				const rule = ['exit-intent', 'inactivity-time'].find((rule) => this.unavailableRules.includes(rule));

				if (rule) {
					this.setUpgradeRule(rule);
				}
				return this.$modal.show('upgrade-modal');
			}

			this.$modal.show('create-campaign-modal');
		},
	},
};
</script>
<style lang="scss">
@import '../assets/scss/pages/playbooks.scss';
</style>
